/**
 * Converts Bazaraki browser page URLs to adverts-geometry API URLs.
 * Allows users to paste any Bazaraki search URL from their browser
 * instead of having to extract API URLs from the network tab.
 */

// Complete rubric mapping for all Bazaraki real estate categories
const BAZARAKI_RUBRIC_MAP = {
  // For Sale
  'real-estate-for-sale/houses': '678',
  'real-estate-for-sale/apartments-flats': '3528',
  'real-estate-for-sale/plots-of-land': '141',
  'real-estate-for-sale/commercial-property': '2405',
  'real-estate-for-sale/residential-buildings': '2790',
  'real-estate-for-sale/prefabricated-houses': '3303',
  'real-estate-for-sale/other': '142',
  // For Rent
  'real-estate-to-rent/houses': '681',
  'real-estate-to-rent/apartments-flats': '3529',
  'real-estate-to-rent/commercial-property': '2408',
  'real-estate-to-rent/plots-of-land': '3530',
  'real-estate-to-rent/rooms-flatmates': '2191',
  'real-estate-to-rent/short-term': '434',
  'real-estate-to-rent/other': '3531',
};

// Tracking/analytics query params to strip (everything else passes through)
const TRACKING_PARAMS = new Set([
  '_gl', '_ga', '_gid', '_gat',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'fbclid', 'gclid', 'msclkid',
]);

/**
 * Check if a URL is a Bazaraki real-estate page/browse URL (not already an API URL).
 * @param {string} url
 * @returns {boolean}
 */
export function isBazarakiPageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url.trim());
    if (!/^(www\.)?bazaraki\.com$/i.test(parsed.hostname)) return false;
    return /^\/(real-estate-for-sale|real-estate-to-rent)\//i.test(parsed.pathname);
  } catch {
    return false;
  }
}

/**
 * Convert a Bazaraki browser page URL to an adverts-geometry API URL.
 *
 * Example input:
 *   https://www.bazaraki.com/real-estate-for-sale/houses/condition---20/number-of-bedrooms---4/?ordering=newest&lat=34.94&lng=33.59&radius=2000
 *
 * Example output:
 *   https://www.bazaraki.com/api/items/adverts-geometry/?rubric=678&ordering=newest&lat=34.94&lng=33.59&radius=2000&attrs__condition=20&attrs__number-of-bedrooms=4&zoom_level=8
 *
 * @param {string} pageUrl - Bazaraki browser URL
 * @returns {string|null} - Converted API URL, or null if conversion fails
 */
export function convertBazarakiPageUrl(pageUrl) {
  if (!isBazarakiPageUrl(pageUrl)) return null;

  try {
    const parsed = new URL(pageUrl.trim());

    // Parse path segments: /real-estate-for-sale/houses/condition---20/...
    const segments = parsed.pathname.split('/').filter(Boolean);

    if (segments.length < 2) return null;

    // First two segments form the category key
    const categoryKey = `${segments[0]}/${segments[1]}`.toLowerCase();
    const rubric = BAZARAKI_RUBRIC_MAP[categoryKey];

    if (!rubric) return null;

    // Build API query params
    const apiParams = new URLSearchParams();
    apiParams.set('rubric', rubric);

    // Pass through all original query params except tracking params
    for (const [key, value] of parsed.searchParams.entries()) {
      if (!TRACKING_PARAMS.has(key)) {
        apiParams.append(key, value);
      }
    }

    // Convert path filter segments to attrs__ query params
    // Filter segments contain '---' separator: condition---20 → attrs__condition=20
    for (let i = 2; i < segments.length; i++) {
      const segment = decodeURIComponent(segments[i]);
      const separatorIndex = segment.indexOf('---');
      if (separatorIndex > 0) {
        const attrName = segment.substring(0, separatorIndex);
        const attrValue = segment.substring(separatorIndex + 3);
        if (attrName && attrValue) {
          apiParams.append(`attrs__${attrName}`, attrValue);
        }
      }
      // Non-filter segments (location slugs, pagination, etc.) are skipped
    }

    // Add default zoom_level for the map API if not already present
    if (!apiParams.has('zoom_level')) {
      apiParams.set('zoom_level', '8');
    }

    return `https://www.bazaraki.com/api/items/adverts-geometry/?${apiParams.toString()}`;
  } catch (err) {
    console.error('[BazarakiUrlConverter] Conversion failed:', err);
    return null;
  }
}

/**
 * Get the list of supported category paths for display purposes.
 * @returns {string[]}
 */
export function getSupportedCategories() {
  return Object.keys(BAZARAKI_RUBRIC_MAP);
}
